import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import MuiAlert from "@mui/material/Alert";
import { useAuth } from "../../../context/AuthContext";
import {
  getTiposAccesorioCatalogo,
  getModelosPorTipoCatalogo,
  resolverAccesorioCatalogo,
  pasarAccesorioALaboratorio,
} from "../services/registroEquiposService";
import { CIUDADES, normalizeCiudad } from "./EquipoSerializadoForm";

const controlSx = {
  "& .MuiInputLabel-root": { fontSize: 12.5 },
  "& .MuiOutlinedInput-root": { fontSize: 13 },
};

const initialValues = { id_servicio_detalle: "", id_equipo: "", cantidad: "", observacion: "" };

// Paso "Almacén -> Laboratorio" que faltaba: mueve cantidad de `almacen` (stock sin
// revisar) a `buen_estado` (lo único que descuenta la Entrega de Accesorios). Usa el mismo
// catálogo Tipo->Modelo que Registrar Accesorio para ubicar la fila exacta en equipo_accesorio.
export default function TransferirAccesorioForm() {
  const { user } = useAuth();
  const [tipos, setTipos] = useState([]);
  const [modelos, setModelos] = useState([]);
  const [cargandoModelos, setCargandoModelos] = useState(false);
  const [stock, setStock] = useState(null);
  const [cargandoStock, setCargandoStock] = useState(false);
  const [values, setValues] = useState({ ...initialValues, ciudad: normalizeCiudad(user?.sucursal) });
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState("");
  const [mensajeExito, setMensajeExito] = useState("");

  useEffect(() => {
    if (!user?.sucursal) return;
    setValues((prev) => (prev.ciudad ? prev : { ...prev, ciudad: normalizeCiudad(user.sucursal) }));
  }, [user?.sucursal]);

  useEffect(() => {
    getTiposAccesorioCatalogo()
      .then(({ data }) => setTipos(Array.isArray(data) ? data : []))
      .catch(() => setTipos([]));
  }, []);

  useEffect(() => {
    if (!values.id_servicio_detalle) {
      setModelos([]);
      return;
    }
    let active = true;
    setCargandoModelos(true);
    getModelosPorTipoCatalogo(values.id_servicio_detalle)
      .then(({ data }) => {
        if (active) setModelos(Array.isArray(data) ? data : []);
      })
      .catch(() => {
        if (active) setModelos([]);
      })
      .finally(() => {
        if (active) setCargandoModelos(false);
      });
    return () => {
      active = false;
    };
  }, [values.id_servicio_detalle]);

  useEffect(() => {
    const { id_servicio_detalle, id_equipo, ciudad } = values;
    if (!id_servicio_detalle || !id_equipo || !ciudad) {
      setStock(null);
      return;
    }
    let active = true;
    setCargandoStock(true);
    resolverAccesorioCatalogo({ id_servicio_detalle, id_equipo, ciudad })
      .then(({ data }) => {
        if (active) setStock(data);
      })
      .catch(() => {
        if (active) setStock(null);
      })
      .finally(() => {
        if (active) setCargandoStock(false);
      });
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.id_servicio_detalle, values.id_equipo, values.ciudad]);

  const setValue = (name, value) => setValues((prev) => ({ ...prev, [name]: value }));

  const onTipoChange = (value) => {
    setValues((prev) => ({ ...prev, id_servicio_detalle: value, id_equipo: "" }));
  };

  const requiredMissing =
    !values.id_servicio_detalle ||
    !values.id_equipo ||
    !values.ciudad.trim() ||
    !values.cantidad ||
    Number(values.cantidad) <= 0 ||
    !stock?.id;

  const guardar = async () => {
    setGuardando(true);
    setError("");
    try {
      const { data } = await pasarAccesorioALaboratorio(stock.id, {
        cantidad: Number(values.cantidad),
        observacion: values.observacion || undefined,
        usu: user?.username,
      });
      setValues({ ...initialValues, ciudad: values.ciudad });
      setModelos([]);
      setStock(null);
      setMensajeExito(`"${data.modelo}" — Almacén: ${data.almacen}, Laboratorio: ${data.buen_estado}.`);
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || "No se pudo transferir el stock.");
    } finally {
      setGuardando(false);
    }
  };

  return (
    <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 1, maxWidth: 720 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 1.25 }}>
        Transferir Almacén → Laboratorio
      </Typography>

      {!!error && (
        <MuiAlert severity="error" variant="outlined" sx={{ mb: 1.25, fontSize: 12.5 }}>
          {error}
        </MuiAlert>
      )}

      <Grid container spacing={1.25}>
        <Grid size={{ xs: 12, sm: 6 }}>
          <FormControl fullWidth size="small" required sx={controlSx}>
            <InputLabel>Tipo de accesorio</InputLabel>
            <Select label="Tipo de accesorio" value={values.id_servicio_detalle} onChange={(e) => onTipoChange(e.target.value)}>
              {tipos.map((t) => (
                <MenuItem key={t.id_servicio_detalle} value={t.id_servicio_detalle}>
                  {t.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <FormControl fullWidth size="small" required sx={controlSx} disabled={!values.id_servicio_detalle || cargandoModelos}>
            <InputLabel>Modelo específico</InputLabel>
            <Select label="Modelo específico" value={values.id_equipo} onChange={(e) => setValue("id_equipo", e.target.value)}>
              {modelos.map((m) => (
                <MenuItem key={m.id_equipo} value={m.id_equipo}>
                  {m.nombre || m.descripcion}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <FormControl fullWidth size="small" required sx={controlSx}>
            <InputLabel>Ciudad</InputLabel>
            <Select label="Ciudad" value={values.ciudad} onChange={(e) => setValue("ciudad", e.target.value)}>
              {CIUDADES.map((c) => (
                <MenuItem key={c} value={c}>
                  {c}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField
            label="Cantidad a transferir"
            value={values.cantidad}
            onChange={(e) => setValue("cantidad", e.target.value)}
            required
            type="number"
            inputProps={{ min: 1 }}
            fullWidth
            size="small"
            sx={controlSx}
          />
        </Grid>
        <Grid size={{ xs: 12 }}>
          <TextField
            label="Observación"
            value={values.observacion}
            onChange={(e) => setValue("observacion", e.target.value)}
            multiline
            minRows={3}
            fullWidth
            size="small"
            sx={controlSx}
          />
        </Grid>
      </Grid>

      <Box sx={{ mt: 1.25 }}>
        {cargandoStock ? (
          <CircularProgress size={16} />
        ) : stock ? (
          <Stack direction="row" spacing={1}>
            <Chip size="small" label={`En Almacén: ${stock.almacen}`} color={stock.almacen > 0 ? "default" : "warning"} />
            <Chip size="small" label={`En Laboratorio: ${stock.buen_estado}`} variant="outlined" />
            {!stock.id && <Chip size="small" label="Sin stock registrado para esta combinación" color="warning" variant="outlined" />}
          </Stack>
        ) : null}
      </Box>

      <Box sx={{ mt: 1.5, display: "flex", justifyContent: "flex-end" }}>
        <Button
          variant="contained"
          color="success"
          disabled={requiredMissing || guardando}
          onClick={guardar}
          startIcon={guardando ? <CircularProgress size={16} color="inherit" /> : null}
        >
          Transferir
        </Button>
      </Box>

      <Snackbar
        open={!!mensajeExito}
        autoHideDuration={4000}
        onClose={() => setMensajeExito("")}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <MuiAlert onClose={() => setMensajeExito("")} severity="success" variant="filled" sx={{ width: "100%" }}>
          {mensajeExito}
        </MuiAlert>
      </Snackbar>
    </Paper>
  );
}
